import React from "react";
import PropTypes from "prop-types";
import { RadioGroup, Radio, Tooltip, Checkbox, Spinner, Input } from "@chakra-ui/react";
import { InfoFormDefault, TimesIcon } from "~misc/icons";
import PercentageSpc from "~components/Form/components/PercentageSpc";
import CurrencyInputAlternate from "~components/Form/components/CurrencyInputAlternate";
import PercentageInput from "~components/Form/components/PercentageInput";
import Select from "~components/Form/components/Select";
import { money } from "~hooks/useHelpers";

const NoInfoOverlay = ({ name, setShowOverlay }) => {
  const overlayRef = React.useRef(null);

  const handleClick = () => {
    setShowOverlay((prev) => ({ ...prev, [name]: false }));

    try {
      const parentElement = overlayRef.current?.parentElement;
      if (parentElement?.children?.[0]) {
        const firstChild = parentElement.children[0];
        const inputs = firstChild.getElementsByTagName("input");
        if (inputs[0]?.focus) inputs[0].focus();
      }
    } catch (error) {
      console.warn("Error focusing input element:", error);
    }
  };

  return (
    <div className="absolute w-full top-0 bg-[#E6E6E6]" ref={overlayRef}>
      <Input onClick={handleClick} className="text-center" style={{ fontSize: 14 }} readOnly />
    </div>
  );
};

NoInfoOverlay.propTypes = {
  name: PropTypes.string.isRequired,
  setShowOverlay: PropTypes.func.isRequired,
};

const ProposalTypeSection = ({
  values,
  handleChange,
  handleChangeTipoProposta,
  enableBeneficio,
  enableFetchBeneficioFiscal,
  setEnableFetchBeneficioFiscal,
}) => (
  <div className="card">
    <h2 className="title">Tipo de Proposta</h2>
    <div className="grid grid-cols-2">
      <div data-test-id="div-radios-proposta">
        <RadioGroup className="inputs-row" onChange={handleChangeTipoProposta} value={values.tipoProposta}>
          <label className="input-container inline" data-test-id="radio-faixa">
            <Radio onChange={handleChange} name="tipoProposta" value="faixa" className="input" />
            <span className="desc">Faixa</span>
            <Tooltip label="Desconto percentual que se aplica ao cliente e ao material, independente do centro e do modal escolhidos.">
              <InfoFormDefault />
            </Tooltip>
          </label>

          <label className="input-container inline" data-test-id="radio-cond-especial">
            <Radio onChange={handleChange} name="tipoProposta" value="condicaoEspecial" className="input" />
            <span className="desc">
              Condição <br /> especial
            </span>
            <Tooltip label="Desconto de preço ou prazo que se aplica especificamente a um cliente, material, centro e modal escolhidos.">
              <InfoFormDefault />
            </Tooltip>
          </label>
        </RadioGroup>
      </div>

      {Boolean(enableBeneficio) && (
        <div className="replicate-inputs flex justify-end">
          <label className="label">
            <Checkbox
              isChecked={enableFetchBeneficioFiscal}
              className="input"
              onChange={() => setEnableFetchBeneficioFiscal(!enableFetchBeneficioFiscal)}
              data-test-id="checkbox-beneficio-fiscal"
            />
            <span className="desc">Incluir Benefício Fiscal</span>
          </label>
        </div>
      )}
    </div>
  </div>
);

ProposalTypeSection.propTypes = {
  values: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleChangeTipoProposta: PropTypes.func.isRequired,
  enableBeneficio: PropTypes.bool,
  enableFetchBeneficioFiscal: PropTypes.bool,
  setEnableFetchBeneficioFiscal: PropTypes.func,
};

const PriceCell = ({
  type,
  price,
  setPrice,
  discount,
  setDiscount,
  showOverlay,
  setShowOverlay,
  setIsEditingDesconto,
  setIsEditingPrecoFinal,
}) => {
  const isFob = type === "fob";
  const overlayKey = isFob ? "fob" : "cif";

  return (
    <td className="p-2 sm:p-4 w-1/3">
      <div className="relative">
        <CurrencyInputAlternate
          type="text"
          inputName={`precoFinal${type.toUpperCase()}`}
          input={price}
          setInput={setPrice}
          center={true}
          onFocus={() => {
            setIsEditingDesconto(false);
            setIsEditingPrecoFinal(true);
          }}
        />

        {!showOverlay[overlayKey] && price !== 0 && (
          <button
            type="button"
            onClick={() => {
              setPrice(0);
              setDiscount(-100);
              setShowOverlay((prev) => ({ ...prev, [overlayKey]: true }));
            }}
            className="absolute cursor-pointer right-0 top-0 h-full flex justify-center items-center z-[1]"
          >
            <TimesIcon fill="#000" />
          </button>
        )}
        {showOverlay[overlayKey] && <NoInfoOverlay name={overlayKey} setShowOverlay={setShowOverlay} />}
      </div>
    </td>
  );
};

PriceCell.propTypes = {
  type: PropTypes.oneOf(["fob", "cif"]).isRequired,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setPrice: PropTypes.func.isRequired,
  discount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setDiscount: PropTypes.func.isRequired,
  showOverlay: PropTypes.object.isRequired,
  setShowOverlay: PropTypes.func.isRequired,
  setIsEditingDesconto: PropTypes.func.isRequired,
  setIsEditingPrecoFinal: PropTypes.func.isRequired,
};

const DiscountCell = ({ type, discount, setDiscount, setIsEditingDesconto, setIsEditingPrecoFinal, ...rest }) => (
  <td className="p-2 sm:p-4 w-1/3" {...rest}>
    <PercentageSpc
      value={`${discount}`}
      inputName={`desconto${type.toUpperCase()}`}
      input={discount}
      setInput={setDiscount}
      center={true}
      onFocus={() => {
        setIsEditingDesconto(true);
        setIsEditingPrecoFinal(false);
      }}
    />
  </td>
);

DiscountCell.propTypes = {
  type: PropTypes.oneOf(["fob", "cif"]).isRequired,
  discount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setDiscount: PropTypes.func.isRequired,
  setIsEditingDesconto: PropTypes.func.isRequired,
  setIsEditingPrecoFinal: PropTypes.func.isRequired,
};

const FiscalBenefitRow = ({
  enableFetchBeneficioFiscal,
  enableBeneficio,
  loadingBeneficio,
  valorBeneficioFiscal,
  wasBeneficioChecked,
}) => {
  if (!enableFetchBeneficioFiscal) return null;

  if (!enableBeneficio) {
    return wasBeneficioChecked ? (
      <tr>
        <td colSpan={3} className="text-red-500 text-center font-bold">
          Só é possível calcular preço com Benefício Fiscal em negociações já aprovadas!
        </td>
      </tr>
    ) : null;
  }

  return (
    <tr className="bg-row4">
      <td className="p-2 sm:p-4 w-1/3">Preço c/ Benefício Fiscal (R$/sc)</td>
      <td className="p-2 sm:p-4 w-1/3">
        <span className="block text-center">
          {loadingBeneficio ? (
            <Spinner size="md" />
          ) : valorBeneficioFiscal?.fob !== 0 ? (
            money(valorBeneficioFiscal.fob)
          ) : (
            "Sem benefício fiscal encontrado!"
          )}
        </span>
      </td>
      <td className="p-2 sm:p-4 w-1/3">
        <span className="block text-center">
          {loadingBeneficio ? (
            <Spinner size="md" />
          ) : valorBeneficioFiscal?.cif !== 0 ? (
            money(valorBeneficioFiscal.cif)
          ) : (
            "Sem benefício fiscal encontrado!"
          )}
        </span>
      </td>
    </tr>
  );
};

FiscalBenefitRow.propTypes = {
  enableFetchBeneficioFiscal: PropTypes.bool,
  enableBeneficio: PropTypes.bool,
  loadingBeneficio: PropTypes.bool,
  valorBeneficioFiscal: PropTypes.object,
  wasBeneficioChecked: PropTypes.bool,
};

const PaymentTermSection = ({
  optionPrazosPagamento,
  onChangePrazoPagamento,
  setPrazoPagamentoObj,
  prazoPagamentoSelect,
  errors,
  touched,
  formikRef,
  onChangeEnviaPrazo,
}) => (
  <div>
    <div className="bg-primaria rounded-tr-lg rounded-tl-lg mt-10">
      <p className="p-[16px] bg-header text-white font-bold">Prazo</p>
    </div>

    <div className="flex flex-col sm:flex-row gap-x-5 p-4 items-center">
      <p className="whitespace-nowrap">Prazo Diferenciado</p>
      <div className="w-[100%] mt-4">
        <Select
          name="prazoPagamentoId"
          label=""
          placeholder=""
          options={optionPrazosPagamento}
          noOptionsMessage="Prazo não encontrado"
          onChange={(e) => {
            onChangePrazoPagamento(e);
            setPrazoPagamentoObj((prev) => ({
              ...prev,
              prazoPagamentoCifValor: e.value,
              prazoPagamentoFobValor: e.value,
            }));
          }}
          errors={errors.prazoPagamentoId}
          touched={touched.prazoPagamentoId}
          {...prazoPagamentoSelect}
          data-test-id="select-payment-terms"
        />
      </div>

      <div className="flex gap-x-4">
        <CheckboxOption
          name="prazoCif"
          label="CIF"
          formikRef={formikRef}
          setPrazoPagamentoObj={setPrazoPagamentoObj}
          onChangeEnviaPrazo={onChangeEnviaPrazo}
          type="cif"
          testId="checkbox-cif-lead-time"
        />
        <CheckboxOption
          name="prazoFob"
          label="FOB"
          formikRef={formikRef}
          setPrazoPagamentoObj={setPrazoPagamentoObj}
          onChangeEnviaPrazo={onChangeEnviaPrazo}
          type="fob"
          testId="checkbox-fob-lead-time"
        />
      </div>
    </div>
  </div>
);

PaymentTermSection.propTypes = {
  optionPrazosPagamento: PropTypes.arrayOf(PropTypes.object),
  onChangePrazoPagamento: PropTypes.func,
  setPrazoPagamentoObj: PropTypes.func,
  prazoPagamentoSelect: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  formikRef: PropTypes.object,
  onChangeEnviaPrazo: PropTypes.func,
};

const CheckboxOption = ({ name, label, formikRef, setPrazoPagamentoObj, onChangeEnviaPrazo, type, testId }) => (
  <div className="flex items-center gap-x-2">
    <label className="flex items-center space-x-2" data-test-id={testId}>
      <Checkbox
        name={name}
        isChecked={formikRef.current?.values[name]}
        onChange={(e) => {
          const value = e.target.checked ? type : "";
          setPrazoPagamentoObj((prev) => ({
            ...prev,
            [`prazoPagamento${type.toUpperCase()}`]: value,
          }));
          onChangeEnviaPrazo(e, type);
        }}
      />
      <span className="text-lg font-bold">{label}</span>
    </label>
  </div>
);

CheckboxOption.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  formikRef: PropTypes.object,
  setPrazoPagamentoObj: PropTypes.func,
  onChangeEnviaPrazo: PropTypes.func,
  type: PropTypes.oneOf(["cif", "fob"]).isRequired,
};

const CondicaoEspecialTable = (props) => (
  <div className="containerForm container bg-white p-7 rounded-lg mt-4 relative negotion-container">
    <div className="">
      <h2 className="">Negociação</h2>
    </div>
    <div className="flex justify-between items-center concorrente proposta gap-x-4 mt-4">
      <div className="container mx-auto">
        <div className="overflow-x-auto table-simulators">
          <table className="w-full bg-white rounded-lg">
            <thead>
              <tr>
                <th className="p-2 sm:p-4 bg-header text-white">Preços</th>
                <th className="p-2 sm:p-4 bg-header text-white">FOB</th>
                <th className="p-2 sm:p-4 bg-header text-white">CIF</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-row1">
                <td className="p-2 sm:p-4">Preço Regra (R$/sc)</td>
                <td className="p-2 sm:p-4">
                  <span className="block text-center">{money(props.precoRegraFob ?? 0)}</span>
                </td>
                <td className="p-2 sm:p-4">
                  <span className="block text-center">{money(props.precoRegraCif ?? 0)}</span>
                </td>
              </tr>
              <tr className="bg-row2">
                <td className="p-2 sm:p-4 w-1/3">Desconto Negociado</td>
                <DiscountCell
                  type="fob"
                  discount={props.descontoFob}
                  setDiscount={props.setDescontoFob}
                  setIsEditingDesconto={props.setIsEditingDesconto}
                  setIsEditingPrecoFinal={props.setIsEditingPrecoFinal}
                  data-test-id="input-discount-fob"
                />
                <DiscountCell
                  type="cif"
                  discount={props.descontoCif}
                  setDiscount={props.setDescontoCif}
                  setIsEditingDesconto={props.setIsEditingDesconto}
                  setIsEditingPrecoFinal={props.setIsEditingPrecoFinal}
                  data-test-id="input-discount-cif"
                />
              </tr>
              <tr className="bg-row3">
                <td className="p-2 sm:p-4 w-1/3">Preço c/ Desconto (R$/sc)</td>
                <PriceCell
                  type="fob"
                  price={props.precoFinalFob}
                  setPrice={props.setPrecoFinalFob}
                  discount={props.descontoFob}
                  setDiscount={props.setDescontoFob}
                  showOverlay={props.showOverlay}
                  setShowOverlay={props.setShowOverlay}
                  setIsEditingDesconto={props.setIsEditingDesconto}
                  setIsEditingPrecoFinal={props.setIsEditingPrecoFinal}
                />
                <PriceCell
                  type="cif"
                  price={props.precoFinalCif}
                  setPrice={props.setPrecoFinalCif}
                  discount={props.descontoCif}
                  setDiscount={props.setDescontoCif}
                  showOverlay={props.showOverlay}
                  setShowOverlay={props.setShowOverlay}
                  setIsEditingDesconto={props.setIsEditingDesconto}
                  setIsEditingPrecoFinal={props.setIsEditingPrecoFinal}
                />
              </tr>
              <FiscalBenefitRow
                enableFetchBeneficioFiscal={props.enableFetchBeneficioFiscal}
                enableBeneficio={props.enableBeneficio}
                loadingBeneficio={props.loadingBeneficio}
                valorBeneficioFiscal={props.valorBeneficioFiscal}
                wasBeneficioChecked={props.wasBeneficioChecked}
              />
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <PaymentTermSection {...props} />
  </div>
);

CondicaoEspecialTable.propTypes = {
  precoRegraFob: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  precoRegraCif: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  descontoFob: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setDescontoFob: PropTypes.func,
  descontoCif: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setDescontoCif: PropTypes.func,
  precoFinalFob: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setPrecoFinalFob: PropTypes.func,
  precoFinalCif: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setPrecoFinalCif: PropTypes.func,
  showOverlay: PropTypes.object,
  setShowOverlay: PropTypes.func,
  setIsEditingDesconto: PropTypes.func,
  setIsEditingPrecoFinal: PropTypes.func,
  enableFetchBeneficioFiscal: PropTypes.bool,
  enableBeneficio: PropTypes.bool,
  loadingBeneficio: PropTypes.bool,
  valorBeneficioFiscal: PropTypes.object,
  wasBeneficioChecked: PropTypes.bool,
  optionPrazosPagamento: PropTypes.arrayOf(PropTypes.object),
  onChangePrazoPagamento: PropTypes.func,
  setPrazoPagamentoObj: PropTypes.func,
  prazoPagamentoSelect: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  formikRef: PropTypes.object,
  onChangeEnviaPrazo: PropTypes.func,
};

const FaixaTable = (props) => (
  <div className="containerForm container bg-white p-7 rounded-lg mt-4 relative negotion-container">
    <div className="">
      <h2 className="">Negociação</h2>
    </div>
    <div className="flex justify-between items-center concorrente proposta gap-x-4 mt-4">
      <div className="container mx-auto">
        <div className="overflow-x-auto table-simulators">
          <table className="w-full bg-white rounded-lg">
            <thead>
              <tr>
                <th className="p-2 sm:p-4 bg-header text-white">Preços</th>
                <th className="p-2 sm:p-4 bg-header text-white">FOB</th>
                <th className="p-2 sm:p-4 bg-header text-white">CIF</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-row1">
                <td className="p-2 sm:p-4">Preço Regra (R$/sc)</td>
                <td className="p-2 sm:p-4">
                  <span className="block text-center">{money(props.precoRegraFob ?? 0)}</span>
                </td>
                <td className="p-2 sm:p-4">
                  <span className="block text-center">{money(props.precoRegraCif ?? 0)}</span>
                </td>
              </tr>
              <tr className="bg-row2">
                <td className="p-2 sm:p-4 w-1/3">Desconto Negociado </td>
                <td className="p-2 sm:p-4" colSpan={2}>
                  <PercentageInput
                    type="default"
                    value={`${props.descontoFob}`}
                    inputName="descontoFobCif"
                    diff={true}
                    input={props.descontoFob}
                    min={-100}
                    setInput={(e) => {
                      props.setDescontoFob(e);
                      props.setDescontoCif(e);
                    }}
                    inputEditable
                  />
                </td>
              </tr>
              <tr className="bg-row3">
                <td className="p-2 sm:p-4 w-1/3">Preço c/ Desconto (R$/sc)</td>
                <td className="p-2 sm:p-4 w-1/3">
                  <span className="block text-center">{money(props.precoFinalFob)}</span>
                </td>
                <td className="p-2 sm:p-4 w-1/3">
                  <span className="block text-center">{money(props.precoFinalCif)}</span>
                </td>
              </tr>
              <FiscalBenefitRow
                enableFetchBeneficioFiscal={props.enableFetchBeneficioFiscal}
                enableBeneficio={props.enableBeneficio}
                loadingBeneficio={props.loadingBeneficio}
                valorBeneficioFiscal={props.valorBeneficioFiscal}
                wasBeneficioChecked={props.wasBeneficioChecked}
              />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);

FaixaTable.propTypes = {
  precoRegraFob: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  precoRegraCif: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  descontoFob: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setDescontoFob: PropTypes.func,
  descontoCif: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setDescontoCif: PropTypes.func,
  precoFinalFob: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setPrecoFinalFob: PropTypes.func,
  precoFinalCif: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setPrecoFinalCif: PropTypes.func,
  enableFetchBeneficioFiscal: PropTypes.bool,
  enableBeneficio: PropTypes.bool,
  loadingBeneficio: PropTypes.bool,
  valorBeneficioFiscal: PropTypes.object,
  wasBeneficioChecked: PropTypes.bool,
};

const NegotiationForm = (props) => {
  const { precoRegra, precoRegraCif, precoRegraFob, loading, values } = props;

  if (!precoRegra || !precoRegraCif || !precoRegraFob || loading) {
    return null;
  }

  return (
    <>
      <ProposalTypeSection
        values={values}
        handleChange={props.handleChange}
        handleChangeTipoProposta={props.handleChangeTipoProposta}
        enableBeneficio={props.enableBeneficio}
        enableFetchBeneficioFiscal={props.enableFetchBeneficioFiscal}
        setEnableFetchBeneficioFiscal={props.setEnableFetchBeneficioFiscal}
      />

      {values.tipoProposta !== "faixa" ? <CondicaoEspecialTable {...props} /> : <FaixaTable {...props} />}
    </>
  );
};

NegotiationForm.propTypes = {
  precoRegra: PropTypes.object,
  precoRegraCif: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  precoRegraFob: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.bool,
  values: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleChangeTipoProposta: PropTypes.func.isRequired,
  enableBeneficio: PropTypes.bool,
  enableFetchBeneficioFiscal: PropTypes.bool,
  setEnableFetchBeneficioFiscal: PropTypes.func,
  wasBeneficioChecked: PropTypes.bool,
  descontoFob: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setDescontoFob: PropTypes.func,
  descontoCif: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setDescontoCif: PropTypes.func,
  precoFinalFob: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setPrecoFinalFob: PropTypes.func,
  precoFinalCif: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setPrecoFinalCif: PropTypes.func,
  showOverlay: PropTypes.object,
  setShowOverlay: PropTypes.func,
  setIsEditingDesconto: PropTypes.func,
  setIsEditingPrecoFinal: PropTypes.func,
  valorBeneficioFiscal: PropTypes.object,
  loadingBeneficio: PropTypes.bool,
  optionPrazosPagamento: PropTypes.arrayOf(PropTypes.object),
  onChangePrazoPagamento: PropTypes.func,
  setPrazoPagamentoObj: PropTypes.func,
  prazoPagamentoSelect: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  formikRef: PropTypes.object,
  onChangeEnviaPrazo: PropTypes.func,
};

export default NegotiationForm;
